import {
  forwardRef,
  useId,
  type ChangeEvent,
  type HTMLAttributes,
} from "react";

import "./AttendanceSwitch.scss";

type AttendanceSwitchProps = Omit<
  HTMLAttributes<HTMLFieldSetElement>,
  "onChange"
> & {
  name: string;
  value: boolean;
  onChange: (value: boolean) => void;

  label?: string;
  attendingLabel?: string;
  decliningLabel?: string;

  error?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
};

export const AttendanceSwitch = forwardRef<
  HTMLFieldSetElement,
  AttendanceSwitchProps
>(
  (
    {
      name,
      value,
      onChange,
      label = "Részt veszel az esküvőn?",
      attendingLabel = "Ott leszek az esküvőn!",
      decliningLabel = "Sajnos nem tudok menni.",
      error,
      hint,
      disabled = false,
      required = false,
      className = "",
      ...fieldsetProps
    },
    ref
  ) => {
    const generatedId = useId();
    const attendingId = `${generatedId}-attending`;
    const decliningId = `${generatedId}-declining`;
    const errorId = `${generatedId}-error`;
    const hintId = `${generatedId}-hint`;

    const describedBy = [
      error ? errorId : undefined,
      !error && hint ? hintId : undefined,
    ]
      .filter(Boolean)
      .join(" ");

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value === "true");
    };

    return (
      <fieldset
        {...fieldsetProps}
        ref={ref}
        className={[
          "attendance-switch",
          error ? "attendance-switch--error" : "",
          disabled ? "attendance-switch--disabled" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy || undefined}
      >
        <legend className="attendance-switch__legend">
          {label}

          {required && (
            <span
              className="attendance-switch__required"
              aria-hidden="true"
            >
              *
            </span>
          )}
        </legend>

        <div className="attendance-switch__control">
          <span
            className={[
              "attendance-switch__slider",
              value
                ? "attendance-switch__slider--attending"
                : "attendance-switch__slider--declining",
            ].join(" ")}
            aria-hidden="true"
          />

          <label
            htmlFor={attendingId}
            className={[
              "attendance-switch__option",
              value ? "attendance-switch__option--active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <input
              id={attendingId}
              type="radio"
              name={name}
              value="true"
              checked={value}
              onChange={handleChange}
              required={required}
              disabled={disabled}
            />

            <span className="attendance-switch__option-mark" aria-hidden="true">
              <span />
            </span>

            <span>{attendingLabel}</span>
          </label>

          <label
            htmlFor={decliningId}
            className={[
              "attendance-switch__option",
              !value ? "attendance-switch__option--active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <input
              id={decliningId}
              type="radio"
              name={name}
              value="false"
              checked={!value}
              onChange={handleChange}
              required={required}
              disabled={disabled}
            />

            <span className="attendance-switch__option-mark" aria-hidden="true">
              <span />
            </span>

            <span>{decliningLabel}</span>
          </label>
        </div>

        {error ? (
          <p
            id={errorId}
            className="attendance-switch__message attendance-switch__message--error"
            role="alert"
          >
            {error}
          </p>
        ) : hint ? (
          <p
            id={hintId}
            className="attendance-switch__message"
          >
            {hint}
          </p>
        ) : null}
      </fieldset>
    );
  }
);

AttendanceSwitch.displayName = "AttendanceSwitch";